/**
 * PublicEventResultsCard — contestant-facing results for a single event.
 *
 * Mounted on EventDetailPage beneath the draw results. Mirrors the way the
 * draw list appears once the draw is performed: it renders ONLY once there
 * are recorded results (at least one scored run), and it polls so standings
 * update live as the secretary enters times/scores.
 *
 * Display adapts to the event type:
 *   - timed events  → shows final TIMES, lowest first (already sorted server-side)
 *   - scored events → shows final SCORES, highest first
 *
 * Penalties are not broken out here — this is the public placing board, not
 * the score-entry screen.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, AlertTriangle, Ban } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  eventStandingsQueryOptions,
  useEventStandings,
  useEventWinnings,
  type StandingsResponse,
  type StandingEntry,
  type ContestantWinningsEntry,
} from '@/api/extensions/useScoringOps';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';
import { formatCurrency } from '@/utils/format';
import { countPrizeParticipants, formatPrizeShareEach } from '@/utils/prize-share';

interface Props {
  eventId: string;
  /** The event row (for metadata/discipline). Optional — falls back to timed. */
  event?: {
    metadata?: Record<string, unknown> | null;
    trig_event_type_name?: string | null;
    /** Number of rounds — drives the "complete vs still to perform" check. */
    total_rounds?: number | null;
  } | null;
  className?: string;
}

const PLACE_ICON: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

// Row grids. Rows use `minmax(0,1fr)` for the name so a long name truncates
// instead of widening the card. Phones keep place | name | time on the first
// line and move the secondary details (caught cue, per-round times, prize)
// under the name; sm+ lays the same cells out on a single line. A second
// template row is only declared when the row has a second line — an empty
// one would be stretched by the taller place cell spanning into it.
const ROW_GRID = 'grid grid-cols-[2rem_minmax(0,1fr)_auto] items-center gap-x-3';
const ONE_LINE = "[grid-template-areas:'place_name_total']";
const PLACED_DETAILS = "[grid-template-areas:'place_name_total'_'place_details_details']";
const PLACED_SM_TWO_LINES = "sm:[grid-template-areas:'place_name_total'_'place_cue_rounds']";
const PLACED_SM_PRIZE =
  "sm:grid-cols-[2rem_minmax(0,1fr)_auto_auto] sm:[grid-template-areas:'place_name_prize_total']";
const PLACED_SM_PRIZE_TWO_LINES =
  "sm:grid-cols-[2rem_minmax(0,1fr)_auto_auto] sm:[grid-template-areas:'place_name_prize_total'_'place_cue_prize_rounds']";
const WINNINGS_TWO_LINES =
  "[grid-template-areas:'place_name_total'_'place_breakdown_breakdown'] sm:[grid-template-areas:'place_name_total'_'place_breakdown_total']";
// "Yet to perform" is too wide to sit beside a name on a phone — it drops
// under the name there.
const NOT_STARTED_ROW =
  "grid grid-cols-[2rem_minmax(0,1fr)] items-center gap-x-3 [grid-template-areas:'place_name'_'place_status'] sm:grid-cols-[2rem_minmax(0,1fr)_auto] sm:[grid-template-areas:'place_name_status']";

const placedRowGrid = (hasPrize: boolean, hasSecondLine: boolean) => {
  if (!hasPrize && !hasSecondLine) return `${ROW_GRID} ${ONE_LINE}`;
  const sm = hasPrize
    ? hasSecondLine ? PLACED_SM_PRIZE_TWO_LINES : PLACED_SM_PRIZE
    : PLACED_SM_TWO_LINES;
  return `${ROW_GRID} ${PLACED_DETAILS} ${sm}`;
};

/**
 * Tab value for a results scope: 'avg' = overall Average (all rounds),
 * 'r<N>' = round N alone, 'total' = each contestant's winnings summed across
 * every prize scope. A string union so further scope kinds can be added
 * as new members without overloading the numeric round scope.
 */
type ScopeTab = 'avg' | `r${number}` | 'total';

const roundTab = (round: number): ScopeTab => `r${round}`;

/**
 * Standings round scope for a tab: null = Average, a number = that round.
 * The Total Winnings tab has its own endpoint; it keeps the Average standings
 * (banner + prize scopes) underneath, so it maps to null too.
 */
const tabRound = (tab: ScopeTab): number | null =>
  tab === 'avg' || tab === 'total' ? null : Number(tab.slice(1));

/** Short scope label for a winnings breakdown line: 'Avg' or 'R<N>'. */
const breakdownLabel = (round: number | null) => (round == null ? 'Avg' : `R${round}`);

export function PublicEventResultsCard({ eventId, event, className }: Props) {
  // Scope selector: 'avg' = overall Average (all rounds); 'r<N>' = that round
  // alone (re-ranked, paying that round's prizes).
  const [tab, setTab] = useState<ScopeTab>('avg');
  const scope = tabRound(tab);
  const isTotal = tab === 'total';

  // Poll every 15s so the board tracks live scoring without a manual refresh.
  // Only the visible tab polls: on Total Winnings the Average standings stay
  // cached (they still drive the tab strip and the completeness banner).
  const { data, isError, isPlaceholderData } = useEventStandings(
    eventId,
    scope != null ? { round: scope } : undefined,
    { refetchInterval: isTotal ? undefined : 15_000 },
  );

  // The standings on screen and the scope they belong to. On a tab switch the
  // hook keeps the previous scope's rows as placeholder data until the new
  // scope's arrive, so the card never unmounts; pairing the rows with their
  // own scope keeps the banner and run counts consistent with what's shown.
  const shownRef = useRef<{ scope: number | null; data: StandingsResponse } | null>(null);
  if (data && !isPlaceholderData) shownRef.current = { scope, data };
  const shown = shownRef.current;
  const viewScope = shown?.scope ?? null;
  // The selected round's first fetch failed — say so rather than leave the
  // previous scope's rows under this tab.
  const scopeFailed = isError && !data;

  // Which scopes carry prizes — show the toggle only when there's more than one
  // payout board to look at (e.g. round prizes AND an overall average).
  const prizeScopes = shown?.data.prize_scopes;
  const boardTabs: ScopeTab[] = prizeScopes
    ? [
        ...(prizeScopes.average ? (['avg'] as const) : []),
        ...prizeScopes.rounds.map(roundTab),
      ]
    : [];
  const showScopeToggle = boardTabs.length > 1;
  // Total Winnings only means something with 2+ payout boards — a single
  // board already is that list. It sits last, after the round tabs.
  const scopeOptions: ScopeTab[] = showScopeToggle ? [...boardTabs, 'total'] : boardTabs;

  // Warm Total Winnings as soon as its tab is on screen so the first click
  // has data; it only polls while that tab is open.
  const winnings = useEventWinnings(eventId, {
    enabled: showScopeToggle,
    refetchInterval: isTotal ? 15_000 : undefined,
  });
  const winningsRows: ContestantWinningsEntry[] =
    winnings.data?.prizes_visible ? winnings.data.data : [];

  // Prefetch each round tab's standings once so switching to a round is
  // instant. No polling here — only the visible tab polls.
  const queryClient = useQueryClient();
  const prefetchRounds = showScopeToggle ? prizeScopes!.rounds.join(',') : '';
  useEffect(() => {
    if (!prefetchRounds) return;
    for (const round of prefetchRounds.split(',').map(Number)) {
      void queryClient.prefetchQuery(eventStandingsQueryOptions(eventId, { round }));
    }
  }, [queryClient, eventId, prefetchRounds]);

  const scoring = resolveEventScoring(event);
  const standings: StandingEntry[] = shown?.data.data ?? [];
  const scored = standings.filter((s) => s.successful_catches > 0);
  // Contestants who competed but recorded a no-time/no-score (NT/NS). The
  // server already ranks these as a single tie at the trailing place
  // (= number of scored contestants + 1); we only need to render them.
  // Entries with no recorded run yet (total_runs === 0) are excluded — they
  // haven't competed, so they aren't "last place" yet.
  // DID NOT PERFORM — contestants marked DNP during scoring. They stay in the
  // results but sort to the very bottom (below NT/NS), styled like the NT/NS
  // rows (dashed border, muted text) but labelled DNP.
  const dnp = standings.filter((s) => s.did_not_perform && s.total_runs > 0);
  const noScored = standings.filter(
    (s) => s.successful_catches === 0 && s.total_runs > 0 && !s.did_not_perform,
  );
  const ntPlace = noScored.length > 0 ? noScored[0].placement : null;
  // Contestants who haven't competed at all yet (no recorded run).
  const notStarted = standings.filter((s) => s.total_runs === 0);

  // Runs a contestant needs to be "complete". The overall Average view expects
  // every round; an isolated-round scope expects a single run for that round.
  const expectedRuns = viewScope != null ? 1 : Math.max(1, Number(event?.total_rounds) || 1);
  // "Still to perform" = anyone who hasn't finished all their runs. This covers
  // both not-started and partially-scored (multi-round) contestants, so the
  // banner reflects that placings can still shift.
  const remaining = standings.filter((s) => s.total_runs < expectedRuns).length;
  const performedCount = scored.length + noScored.length + dnp.length;
  const allScored = performedCount > 0 && remaining === 0;

  // Opt-in like the draw results card: nothing to show until the event has
  // actually been performed. Auth-gated endpoint → 401/404 collapses to null.
  // That applies to the first load only — a tab switch keeps the card mounted.
  // Once a round scope is selected we keep the card (with its toggle) visible
  // even if that round has no results yet, so the viewer can switch back.
  if (!shown) return null;
  if (
    scored.length === 0 && noScored.length === 0 && dnp.length === 0 &&
    tab === 'avg' && viewScope == null
  ) return null;

  const fmtValue = (v: number | null) => {
    if (v == null) return '—';
    return scoring.isScored ? Number(v).toFixed(1) : `${Number(v).toFixed(timedScoreDecimals(scoring.isBarrelRacing))}s`;
  };

  // The overall Average of a multi-round event ranks by rounds caught/scored
  // first, then total — so a shorter total can place lower. Say so up front.
  const ranksByCount = !isTotal && viewScope == null && expectedRuns > 1;
  const countWord = scoring.isScored ? 'scored' : 'caught';
  const missLabel = scoring.isScored ? 'NS' : 'NT';
  // Which rounds make up an Average total: "R1 5.00s · R2 NT".
  const roundPartLabel = (r: NonNullable<StandingEntry['round_results']>[number]) =>
    r.status === 'ok'
      ? fmtValue(r.value)
      : r.status === 'no_time'
        ? missLabel
        : r.status === 'dq'
          ? 'DQ'
          : r.status === 'dnp'
            ? 'DNP'
            : '—';

  const scopeLabel = (s: number | null) => (s == null ? 'Average (All Rounds)' : `Round ${s}`);
  // Tabs use the short 'Average'; the long form stays as the title/aria-label.
  const tabTitle = (t: ScopeTab) => (t === 'total' ? 'Total Winnings' : scopeLabel(tabRound(t)));
  const tabLabel = (t: ScopeTab) => (t === 'avg' ? 'Average' : tabTitle(t));

  return (
    <Card className={className}>
      <CardHeader className="px-4 pb-3 sm:px-6">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            Results
            <Badge variant="secondary" className="font-normal">
              {ranksByCount
                ? scoring.isScored ? 'Most scored, then high score' : 'Most caught, then fast time'
                : scoring.isScored ? 'High score wins' : 'Fast time wins'}
            </Badge>
          </CardTitle>
          {/* Phones: the tab strip takes a full-width row of its own, with
              taller tabs, and the placed count sits under it. */}
          <div className="flex w-full min-w-0 max-w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center">
            {showScopeToggle && (
              <Tabs value={tab} onValueChange={(v) => setTab(v as ScopeTab)} className="w-full min-w-0 max-w-full sm:w-auto">
                {/* Wraps onto extra rows when an event has many rounds (finals). */}
                <TabsList className="h-auto w-full max-w-full flex-wrap justify-start sm:w-auto">
                  {scopeOptions.map((t) => (
                    <TabsTrigger
                      key={t}
                      value={t}
                      title={tabTitle(t)}
                      aria-label={tabTitle(t)}
                      className="h-9 flex-auto px-1.5 text-xs sm:h-7 sm:flex-none sm:px-2.5"
                    >
                      {tabLabel(t)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            <span className="text-xs font-normal text-muted-foreground">
              {isTotal
                ? `${winningsRows.length} contestant${winningsRows.length === 1 ? '' : 's'} paid · updates live`
                : `${scored.length + noScored.length + dnp.length} placed · updates live`}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {/* Completeness banner — guards against viewers reading provisional
            standings as final while contestants are still to perform. */}
        {!scopeFailed && performedCount > 0 && (
          allScored ? (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/20 dark:text-emerald-300">
              <Trophy className="h-4 w-4 shrink-0" />
              <span>Final results — all contestants have been scored.</span>
            </div>
          ) : (
            <div className="mb-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/20 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                Results are not complete — {remaining} contestant{remaining === 1 ? '' : 's'} still to perform. Placings may change.
              </span>
            </div>
          )
        )}
        {isTotal ? (
          winnings.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : winningsRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Winnings will appear once results are final.
            </p>
          ) : (
            <div className="space-y-1.5">
              {winningsRows.map((w) => {
                const icon = PLACE_ICON[w.placement];
                return (
                  <div
                    key={w.user_profile_id ?? `${w.placement}-${w.name}`}
                    className={`${ROW_GRID} ${w.breakdown.length > 0 ? WINNINGS_TWO_LINES : ONE_LINE} rounded-md border px-3 py-2 ${
                      w.placement <= 3 ? 'border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/20' : ''
                    }`}
                  >
                    <div className="flex items-center gap-1 [grid-area:place]">
                      {icon && <span>{icon}</span>}
                      <span className="font-bold tabular-nums">{w.placement}</span>
                    </div>
                    <p className="min-w-0 truncate text-sm font-semibold [grid-area:name]">{w.name}</p>
                    {w.breakdown.length > 0 && (
                      <p className="text-[11px] text-muted-foreground [grid-area:breakdown]">
                        {w.breakdown
                          .map((b) => `${breakdownLabel(b.round)} ${formatCurrency(b.prize_amount)}`)
                          .join(' · ')}
                      </p>
                    )}
                    <div className="text-right font-mono text-sm font-bold tabular-nums text-emerald-600 [grid-area:total] dark:text-emerald-400">
                      {formatCurrency(w.total_winnings)}
                    </div>
                  </div>
                );
              })}
              {scoring.isTeamRoping && (
                <p className="pt-1 text-[11px] text-muted-foreground">
                  Each roper is credited half of their team&apos;s prize.
                </p>
              )}
            </div>
          )
        ) : scopeFailed ? (
          <p className="text-sm text-muted-foreground">
            Couldn&apos;t load results for {scopeLabel(scope)}.
          </p>
        ) : scored.length === 0 && noScored.length === 0 && dnp.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No results recorded for {scopeLabel(viewScope)} yet.
          </p>
        ) : (
        <div className="space-y-1.5">
          {scored.map((s) => {
            const icon = PLACE_ICON[s.placement];
            // Team entries pay ONE prize to two ropers — show each half's take
            // alongside the team total. Null for solo entries.
            const shareEach = formatPrizeShareEach(
              s.prize_amount,
              countPrizeParticipants(s.member_names),
            );
            // Rounds run but not caught/scored — they don't add to the total,
            // which is why a shorter total can sit below a longer one.
            const missed = s.total_runs - s.successful_catches;
            const roundParts = ranksByCount ? s.round_results ?? [] : [];
            const hasPrize = (s.prize_amount ?? 0) > 0;
            const showCue = expectedRuns > 1;
            return (
              <div
                key={s.team_id}
                className={`${placedRowGrid(hasPrize, showCue || roundParts.length > 0)} rounded-md border px-3 py-2 ${
                  s.placement <= 3 ? 'border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/20' : ''
                }`}
              >
                <div className="flex items-center gap-1 [grid-area:place]">
                  {icon && <span>{icon}</span>}
                  <span className="font-bold tabular-nums">{s.placement}</span>
                </div>
                <p className="min-w-0 truncate text-sm font-semibold [grid-area:name]">
                  {s.excluded_from_prize_pool && (
                    <span title="Excluded from prize pool" aria-label="Excluded from prize pool" className="mr-1 inline-flex align-text-bottom">
                      <Ban className="h-3.5 w-3.5 text-slate-500" />
                    </span>
                  )}
                  {(s.member_names ?? []).filter((n) => n && n !== '—').join(' / ') || 'Entry'}
                </p>
                <div className="text-right font-mono text-sm font-bold tabular-nums [grid-area:total]">
                  {fmtValue(s.total_time)}
                </div>
                {(hasPrize || showCue || roundParts.length > 0) && (
                  // Phones: one wrapping line under the name — cue left,
                  // per-round times right, prize on its own line below.
                  // sm+: `contents`, so each cell takes its own grid area.
                  <div className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-3 [grid-area:details] sm:contents">
                    {showCue && (
                      <p
                        className={`text-[11px] [grid-area:cue] ${
                          missed > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                        }`}
                      >
                        {s.successful_catches}/{expectedRuns} {countWord}
                        {missed > 0 ? ` · ${missLabel} in ${missed} round${missed === 1 ? '' : 's'}` : ''}
                        {s.total_runs < expectedRuns ? ' · still to perform' : ''}
                      </p>
                    )}
                    {roundParts.length > 0 && (
                      <p
                        data-testid="round-results"
                        className="ml-auto max-w-[10rem] text-right text-[11px] tabular-nums text-muted-foreground [grid-area:rounds] sm:ml-0"
                      >
                        {roundParts.map((r, i) => (
                          <Fragment key={r.round}>
                            {i > 0 && ' · '}
                            <span
                              className={`whitespace-nowrap ${
                                r.status === 'ok' ? '' : 'text-amber-700 dark:text-amber-400'
                              }`}
                            >
                              R{r.round} {roundPartLabel(r)}
                            </span>
                          </Fragment>
                        ))}
                      </p>
                    )}
                    {hasPrize && (
                      <div className="basis-full font-mono text-sm font-bold tabular-nums text-emerald-600 [grid-area:prize] dark:text-emerald-400 sm:text-right">
                        {formatCurrency(s.prize_amount as number)}
                        {shareEach && (
                          <span className="ml-1 font-normal text-[11px] text-muted-foreground">
                            ({shareEach})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* No-time / no-score contestants — all tied for last place. They
              share one placement (= scored count + 1) and show NT/NS instead
              of a time. */}
          {noScored.map((s) => (
            <div
              key={s.team_id}
              className={`${ROW_GRID} rounded-md border border-dashed px-3 py-2 text-muted-foreground`}
            >
              <div className="flex items-center gap-1">
                <span className="font-bold tabular-nums">{s.placement}</span>
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {s.excluded_from_prize_pool && (
                    <span title="Excluded from prize pool" aria-label="Excluded from prize pool" className="mr-1 inline-flex align-text-bottom">
                      <Ban className="h-3.5 w-3.5 text-slate-500" />
                    </span>
                  )}
                  {(s.member_names ?? []).filter((n) => n && n !== '—').join(' / ') || 'Entry'}
                </p>
                {ntPlace != null && noScored.length > 1 && (
                  <p className="text-[11px] text-muted-foreground">Tied for last</p>
                )}
              </div>
              <div className="text-right text-xs font-semibold uppercase tracking-wide">
                {scoring.isScored ? 'NS' : 'NT'}
              </div>
            </div>
          ))}

          {/* Contestants who have not competed yet — shown without a placement
              so viewers can see the field isn't complete. */}
          {notStarted.map((s) => (
            <div
              key={s.team_id}
              className={`${NOT_STARTED_ROW} rounded-md border border-dashed px-3 py-2 text-muted-foreground`}
            >
              <div className="flex items-center justify-center [grid-area:place]">
                <span className="text-sm">—</span>
              </div>
              <p className="min-w-0 truncate text-sm font-semibold [grid-area:name]">
                {s.excluded_from_prize_pool && (
                  <span title="Excluded from prize pool" aria-label="Excluded from prize pool" className="mr-1 inline-flex align-text-bottom">
                    <Ban className="h-3.5 w-3.5 text-slate-500" />
                  </span>
                )}
                {(s.member_names ?? []).filter((n) => n && n !== '—').join(' / ') || 'Entry'}
              </p>
              <div className="text-xs font-semibold uppercase tracking-wide [grid-area:status] sm:text-right">
                Yet to perform
              </div>
            </div>
          ))}

          {/* Did-not-perform contestants — sorted to the very bottom. Styled
              like the NT/NS rows (dashed border, muted text), labelled DNP. */}
          {dnp.map((s) => (
            <div
              key={s.team_id}
              className={`${ROW_GRID} rounded-md border border-dashed px-3 py-2 text-muted-foreground`}
            >
              <div className="flex items-center justify-center">
                <span className="text-sm">—</span>
              </div>
              <p className="min-w-0 truncate text-sm font-semibold">
                {s.excluded_from_prize_pool && (
                  <span title="Excluded from prize pool" aria-label="Excluded from prize pool" className="mr-1 inline-flex align-text-bottom">
                    <Ban className="h-3.5 w-3.5 text-slate-500" />
                  </span>
                )}
                {(s.member_names ?? []).filter((n) => n && n !== '—').join(' / ') || 'Entry'}
              </p>
              <div className="text-right text-xs font-semibold uppercase tracking-wide">
                DNP
              </div>
            </div>
          ))}
        </div>
        )}
      </CardContent>
    </Card>
  );
}
